import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Copy, Trash2, Check, Save } from "lucide-react";
import { useBranding } from "@/context/BrandingContext";

const COLORS = [
  ["sidebar", "Seitenleiste", "Hintergrund der linken Spalte"],
  ["sidebar_text", "Text Seitenleiste", "Menüpunkte, App-Name, Benutzername"],
  ["sidebar_muted", "Text Seitenleiste gedämpft", "Rubriken, Untertitel, Rolle, Untermenü"],
  ["nav_active", "Aktiver Menüpunkt", "Hintergrund des gewählten Menüpunkts"],
  ["nav_active_text", "Text aktiver Menüpunkt", "Schrift im gewählten Menüpunkt"],
  ["primary", "Hauptfarbe", "Buttons (weiße Schrift)"],
  ["accent", "Akzentfarbe", "Links und Hervorhebungen im Inhalt"],
  ["background", "Seitenhintergrund", "Hintergrund der Inhaltsseiten"],
  ["heading_text", "Überschriften", "Textfarbe der Überschriften im Inhalt"],
  ["body_text", "Fließtext", "Textfarbe des Fließtexts im Inhalt"],
];
const SWATCH = ["sidebar", "nav_active", "primary", "accent", "background"];
const PAIRS = [
  ["sidebar_text", "sidebar", "Text Seitenleiste", 4.5], ["sidebar_muted", "sidebar", "Gedämpfter Text", 3],
  ["nav_active_text", "nav_active", "Aktiver Menüpunkt", 4.5], ["#FFFFFF", "primary", "Button-Schrift", 4.5],
  ["heading_text", "background", "Überschriften", 4.5], ["body_text", "background", "Fließtext", 4.5],
  ["accent", "background", "Links", 3],
];
const lum = (hex) => {
  const c = (hex || "#000000").replace("#", "").match(/.{2}/g).map((x) => parseInt(x, 16) / 255)
    .map((v) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
  return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
};
const contrast = (a, b) => { const [x, y] = [lum(a), lum(b)].sort((m, n) => n - m); return (x + 0.05) / (y + 0.05); };
const valid = (h) => /^#[0-9A-Fa-f]{6}$/.test(h || "");

function ContrastHints({ form }) {
  const bad = PAIRS.map(([f, b, l, min]) => {
    const fg = f.startsWith("#") ? f : form[f];
    if (!valid(fg) || !valid(form[b])) return null;
    const r = contrast(fg, form[b]);
    return r < min ? `${l}: Kontrast ${r.toFixed(1)}:1 (empfohlen ≥ ${min}:1)` : null;
  }).filter(Boolean);
  if (!bad.length) return <p className="text-xs text-emerald-700" data-testid="contrast-ok">Alle Farbkombinationen sind gut lesbar.</p>;
  return (
    <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="contrast-warnings">
      <div className="font-semibold mb-0.5">Schlecht lesbar:</div>
      {bad.map((b) => <div key={b}>{b}</div>)}
    </div>
  );
}

function Swatches({ p }) {
  return (
    <div className="flex h-8 rounded-md overflow-hidden border border-slate-200">
      {SWATCH.map((k) => <div key={k} className="flex-1" style={{ backgroundColor: p[k] }} />)}
    </div>
  );
}

function ProfileCard({ p, active, selected, onSelect }) {
  return (
    <button type="button" onClick={onSelect} data-testid={`color-profile-${p.id}`}
      className={`text-left bg-white border rounded-xl p-3 shadow-sm transition-colors ${selected ? "border-slate-900 ring-1 ring-slate-900" : "border-slate-200 hover:border-slate-400"}`}>
      <Swatches p={p} />
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="font-semibold text-sm text-slate-800 truncate">{p.name}</span>
        {active && <span className="text-[10px] font-bold uppercase text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5" data-testid="color-profile-active-badge">Aktiv</span>}
      </div>
      <div className="text-xs text-slate-400 mt-0.5 truncate">{p.font_heading} / {p.font_body}</div>
    </button>
  );
}

function ColorField({ k, label, hint, form, setForm }) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex items-center gap-2 mt-1">
        <input type="color" value={valid(form[k]) ? form[k] : "#000000"} onChange={(e) => setForm({ ...form, [k]: e.target.value.toUpperCase() })} className="h-10 w-12 rounded border border-slate-200 cursor-pointer" data-testid={`profile-color-picker-${k}`} />
        <Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mono" data-testid={`profile-color-${k}`} />
      </div>
      <p className="text-xs text-slate-400 mt-1">{hint}</p>
    </div>
  );
}

function FontField({ k, label, fonts, form, setForm }) {
  return (
    <div>
      <Label>{label}</Label>
      <Select value={form[k]} onValueChange={(v) => setForm({ ...form, [k]: v })}>
        <SelectTrigger className="mt-1" data-testid={`profile-font-${k}`}><SelectValue /></SelectTrigger>
        <SelectContent>{fonts.map((f) => <SelectItem key={f} value={f}><span style={{ fontFamily: `'${f}'` }}>{f}</span></SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

export default function ColorProfilesSettings() {
  const [data, setData] = useState(null);
  const [selId, setSelId] = useState(null);
  const [form, setForm] = useState(null);
  const { refresh } = useBranding();

  const apply = (d, keepId) => {
    setData(d);
    const id = keepId && d.profiles.some((p) => p.id === keepId) ? keepId : d.active_id;
    setSelId(id);
    setForm({ ...d.profiles.find((p) => p.id === id) });
  };
  useEffect(() => { api.get("/branding/profiles").then((r) => apply(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail))); }, []);

  const run = async (fn, msg, pick) => {
    try { const { data: d } = await fn(); apply(d, pick ? pick(d) : selId); toast.success(msg); refresh(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const newest = (d) => d.profiles[d.profiles.length - 1].id;
  const body = () => { const { id, created_at, ...rest } = form; return rest; };

  if (!data || !form) return <div className="text-slate-400">Lädt…</div>;
  const isActive = selId === data.active_id;

  return (
    <div className="max-w-4xl space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-slate-500">Das aktive Profil gilt für alle Nutzer. Schriften werden lokal vom eigenen Server geladen.</p>
        <Button variant="outline" onClick={() => run(() => api.post("/branding/profiles", { ...body(), name: "Neues Profil" }), "Profil angelegt", newest)} data-testid="color-profile-new"><Plus size={15} className="mr-1.5" /> Neues Profil</Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="color-profile-list">
        {data.profiles.map((p) => (
          <ProfileCard key={p.id} p={p} active={p.id === data.active_id} selected={p.id === selId} onSelect={() => { setSelId(p.id); setForm({ ...p }); }} />
        ))}
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-5" data-testid="color-profile-editor">
        <div>
          <Label>Profilname</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="profile-name" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {COLORS.map(([k, l, h]) => <ColorField key={k} k={k} label={l} hint={h} form={form} setForm={setForm} />)}
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <FontField k="font_heading" label="Schrift Überschriften" fonts={data.fonts} form={form} setForm={setForm} />
          <FontField k="font_body" label="Schrift Fließtext" fonts={data.fonts} form={form} setForm={setForm} />
        </div>
        <div className="rounded-lg overflow-hidden border border-slate-200 flex h-36" data-testid="color-profile-preview">
          <div className="w-36 p-3 space-y-2" style={{ backgroundColor: form.sidebar }}>
            <div className="text-xs font-bold" style={{ color: form.sidebar_text }}>App-Name</div>
            <div className="text-[9px] tracking-widest" style={{ color: form.sidebar_muted }}>RUBRIK</div>
            <div className="text-xs rounded px-2 py-1" style={{ backgroundColor: form.nav_active, color: form.nav_active_text }}>Aktiv</div>
            <div className="text-xs px-2" style={{ color: form.sidebar_text }}>Menüpunkt</div>
          </div>
          <div className="flex-1 p-4" style={{ backgroundColor: form.background }}>
            <div className="text-lg font-bold" style={{ fontFamily: `'${form.font_heading}'`, color: form.heading_text }}>Überschrift</div>
            <div className="text-sm" style={{ fontFamily: `'${form.font_body}'`, color: form.body_text }}>Fließtext mit <span style={{ color: form.accent }}>Link</span></div>
            <span className="inline-block mt-2 text-xs text-white rounded px-2 py-1" style={{ backgroundColor: form.primary }}>Button</span>
          </div>
        </div>
        <ContrastHints form={form} />
        <div className="flex flex-wrap gap-2 pt-1">
          <Button onClick={() => run(() => api.put(`/branding/profiles/${selId}`, body()), "Profil gespeichert")} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="color-profile-save"><Save size={15} className="mr-1.5" /> Speichern</Button>
          {!isActive && <Button variant="outline" onClick={() => run(() => api.post(`/branding/profiles/${selId}/activate`), "Profil aktiviert")} data-testid="color-profile-activate"><Check size={15} className="mr-1.5" /> Aktivieren</Button>}
          <Button variant="outline" onClick={() => run(() => api.post(`/branding/profiles/${selId}/copy`), "Profil kopiert", newest)} data-testid="color-profile-copy"><Copy size={15} className="mr-1.5" /> Kopieren</Button>
          {!isActive && <Button variant="outline" onClick={() => { if (window.confirm(`Profil "${form.name}" löschen?`)) run(() => api.delete(`/branding/profiles/${selId}`), "Profil gelöscht"); }} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="color-profile-delete"><Trash2 size={15} className="mr-1.5" /> Löschen</Button>}
        </div>
      </div>
    </div>
  );
}
